<template>
  <article
    class="dashboard-verdict-card"
    role="button"
    tabindex="0"
    @click="goToVerdict"
    @keydown.enter="goToVerdict"
    @keydown.space.prevent="goToVerdict"
  >
    <div class="dashboard-verdict-card__meta">
      <span class="dashboard-verdict-card__id font-mono">{{ id }}</span>
      <span class="dashboard-verdict-card__time font-mono">{{ timeAgo }}</span>
    </div>

    <!-- D3: protocol line — mono ALL CAPS, between id and subject -->
    <span v-if="protocol && protocol !== '—'" class="dashboard-verdict-card__protocol font-mono">
      {{ protocol.toUpperCase() }}
    </span>

    <p class="dashboard-verdict-card__subject">{{ subject }}</p>

    <p class="dashboard-verdict-card__headline">{{ headline }}</p>

    <div class="dashboard-verdict-card__footer">
      <KdsBadge :tone="consensusTone" class="dashboard-verdict-card__badge">
        {{ consensusToneLabel }}
      </KdsBadge>

      <div class="dashboard-verdict-card__personas">
        <span class="dashboard-verdict-card__count font-mono">{{ personaCount }}</span>
        <KdsAvatarStack :segments="avatarSegments" :max="5" :size="20" />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import KdsAvatarStack from '@/kds/primitives/KdsAvatarStack.vue';
import type { KdsBadgeTone, PersonaSegment } from '@/kds/types';

interface Props {
  id: string;
  subject: string;
  /** D3: protocol label shown between id and subject. Empty / '—' hides it. */
  protocol: string;
  timeAgo: string;
  headline: string;
  consensusTone: KdsBadgeTone;
  personaCount: number;
  /** D4: ordered list of PersonaSegment tokens (max 5 shown, +N overflow). */
  avatarSegments: PersonaSegment[];
}

const props = withDefaults(defineProps<Props>(), {
  avatarSegments: () => []
});

const { t } = useI18n();
const router = useRouter();

// Tone label via i18n — shared keys with VerdictCard (D2 — PO decision 2026-05-22).
const consensusToneLabel = computed<string>(() =>
  t(`verdicts.card.toneLabels.${props.consensusTone}`)
);

const goToVerdict = (): void => {
  void router.push({ name: 'verdict', params: { id: props.id } });
};
</script>

<style scoped lang="scss">
@use '@/css/kds-glass' as glass;

/*
 * Exception to DESIGN.md §9 (no glass / frosted surfaces):
 * The dashboard verdict card uses a glassmorphism background so the
 * decorative dashboard-background image remains visible through the cards.
 * Aligned on the shared kds-glass mixin (0.30 opacity) per spec CA-3 / plan H4.
 * Risk R1: opacity changed from 0.70 to 0.30 — validate at review.
 */
.dashboard-verdict-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  @include glass.kds-glass-surface;
  border-radius: var(--r-4);
  cursor: pointer;
  transition:
    border-color var(--d-fast) var(--ease-out),
    box-shadow var(--d-fast) var(--ease-out);

  // D1(a): glass card uses softer hover shadow to avoid heavy elevation over transparent surface.
  &:hover {
    border-color: var(--mist-2);
    box-shadow: var(--shadow-1);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-2);
  }

  &__id {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone);
  }

  &__protocol {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  &__time {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone);
  }

  &__subject {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--ink);
    font-weight: var(--fw-medium);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__headline {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--ink);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  &__personas {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  &__count {
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone);
  }
}
</style>
